import {defineStore} from "pinia"
import {useRequestOptions} from "~/composables/requestOptions"
import type {EventData} from "~/utils/types/event"
import {EventParticipantInformation} from "~/utils/types/event.d"
import {navigateToSummary} from "~/utils/utils"
import { useAccountStore } from "./account"
import {parsePhoneNumberFromString} from "libphonenumber-js"
import type {CheckoutApi, InsuranceData, SeatsIO, SummaryItem} from "~/utils/types/checkout"
import { LineItemType, PaymentIntentStatus } from "~/utils/types/payment_intent.d"
import type {PaymentIntent} from "@stripe/stripe-js";

const requestOptions = useRequestOptions(false)
const requestOptionsPost = useRequestOptions()
const default3cketColor = '#452f87'

export const useCheckoutStore = defineStore('checkout', {
    state: (): CheckoutApi => <CheckoutApi>({
        event: {} as EventData,
        promoter: {} as Promoter,
        pi: {} as PaymentIntent,
        gateways: {} as Gateways,
        line_items: [],
        summary: new Map() as Map<string, SummaryItem>,
        user: {} as CheckoutUser,
        insurance: {} as InsuranceData,
        seats_io: {} as SeatsIO,
        loading: false,
        bar_items: undefined,
        order_type: undefined,
        order_token: undefined,
        order_value: undefined,
        order_id: undefined,
        payment_method: undefined,
    }),

    getters: {
        currency: state => state.pi?.total?.currency?.toLowerCase() || state.event?.currency?.toLowerCase() || 'eur',
        total: state => {
            if (state.pi?.total) {
                return state.pi.total.amount + (state.pi.participant_fee?.amount ?? 0) + (state.insurance?.amounts ? state.insurance.amounts.amount : 0)
            }
            if (state.order_token) {
                return Number(state.order_value ?? 0)
            }
            return 0
        },
        color: (state) => state.event.media.color || default3cketColor,
        isGuestList: (state) => state.pi?.total?.amount === 0,
        requireExtraInfo: (state) => state.event.participant_information === EventParticipantInformation.unnecessary,
        requireLogin: (state) => state.user.registered === false && state.event.participant_information !== EventParticipantInformation.unnecessary,
        numberTotalTickets: (state) => state.line_items.reduce((sum: number, {product}: {product: {ticket: Ticket}}) => sum + ((product.ticket.pack || 0) + 1), 0),
        googleAdsData: (state) => {
            const isGoogleAdsEnabled = state.event.tracking.google_ads?.enabled || state.promoter?.tracking?.google_ads?.enabled
            if(!isGoogleAdsEnabled) return null
            return state.event.tracking.google_ads || state.promoter?.tracking?.google_ads
        },
        isFbEnabled: (state) => state.event.tracking.fb_pixel_internal?.enabled || state.event.tracking.fb_pixel?.enabled || state.promoter?.tracking?.fb_pixel?.enabled,
        fbInternalData: (state) => state.event.tracking.fb_pixel_internal?.enabled ? state.event.tracking.fb_pixel_internal : null,
        fbPixelData: (state) => {
            const isFbPixelEnabled = state.event.tracking.fb_pixel?.enabled || state.promoter?.tracking?.fb_pixel?.enabled
            if(!isFbPixelEnabled) return null
            return state.event.tracking.fb_pixel || state.promoter?.tracking?.fb_pixel
        },
        isTiktokEnabled: (state) => state.event.tracking.tiktok_pixel?.enabled || state.promoter?.tracking?.tiktok_pixel?.enabled,
        tiktokPixelData: (state) => {
            const isTiktokPixelEnabled = state.event.tracking.tiktok_pixel?.enabled || state.promoter?.tracking?.tiktok_pixel?.enabled
            if(!isTiktokPixelEnabled) return null
            return state.event.tracking.tiktok_pixel || state.promoter?.tracking?.tiktok_pixel
        },
        coupon: (state) => {
            for (const item of state.line_items) {
                if (item.amounts?.modifiers?.length) {
                    return item.amounts.modifiers[0];
                }
            }
            return null;
        },
        isInvoiceCTAVisible: (state) => {
          const countryCode = state.event.location.country_code?.toUpperCase() || 'DEFAULT'
          const countryRules: Record<string, boolean> = {
            PT: true,
          }
          return countryRules[countryCode] ?? false
        },
        isInvoicesVisible: (state) => {
          const countryCode = state.event.location.country_code?.toUpperCase() || 'DEFAULT'
          const countryRules: Record<string, boolean> = {
            ES: false,
          }
          return countryRules[countryCode] ?? true
        },
    },

    actions: {
        initCheckout: async function () {

            const dayjs = useDayjs()
            const {slug} = useRoute().params

            const {data: response, error}: any = await useFetch(`/checkout/${slug}`, requestOptions)

            if (error.value || !response.value) throw createError({statusCode: 404, statusMessage: 'Unable to Init Checkout', fatal: true, data: error.value})

            if (response.value.legacy) {
                await navigateToSummary(slug)
                return
            }

            const data = response.value.data

            data.line_items = data.line_items ?? []

            const insurance = data.line_items.find(item => item.type === 'insurance')

            data.line_items = data.line_items.filter(item => item.type !== 'insurance')

            this.$state = data

            if (insurance) {
                if (insurance.attributes && insurance.attributes.quotation_tax) {
                    insurance.attributes.quotation_tax = parseFloat(
                        Number(insurance.attributes.quotation_tax).toFixed(2)
                    );
                }
                this.$state.insurance = insurance
            }

            if (!this.event || !this.pi || !this.line_items) throw createError({statusCode: 404, statusMessage: 'Unable to Init Checkout', fatal: true})

            if (this.pi.status === PaymentIntentStatus.finished) {
                await navigateToSummary(slug)
            }

            this.$state.summary = new Map()
            this.$state.summary = this.getSummaryItems()

            dayjs.locale(this.event.language ?? undefined)

            this.syncUser()
        },

        initSummary: async function () {

            const dayjs = useDayjs()
            const {slug} = useRoute().params

            const {data: response, error}: any = await useFetch(`/checkout/${slug}/summary`, requestOptions)

            if (error.value || !response.value) throw createError({statusCode: 404, statusMessage: 'Unable to Init Checkout', fatal: true, data: error.value})

            if (response.value.legacy) {
                // todo remove this legacy redirect later
                await navigateTo(`https://o.3cket.com/${slug}`, {external: true})
                return
            }

            const data = response.value.data

            data.line_items = data.line_items ?? []

            const insurance = data.line_items.find(item => item.type === 'insurance')
            data.line_items = data.line_items.filter(item => item.type !== 'insurance')

            this.$state = data

            if (insurance) {
                if (insurance.attributes && insurance.attributes.quotation_tax) {
                    insurance.attributes.quotation_tax = parseFloat(
                        Number(insurance.attributes.quotation_tax).toFixed(2)
                    );
                }
                this.$state.insurance = insurance
            }

            if (!this.event) throw createError({statusCode: 404, statusMessage: 'Unable to Init Checkout', fatal: true})

            this.$state.summary = new Map()
            this.$state.summary = this.getSummaryItems()

            dayjs.locale(this.event.language ?? undefined)

            this.syncUser()
        },

        syncUser(): void {
            if (!this.user) return

            const account = useAccountStore()
            account.user = this.user
            account.registered = this.user.registered

            if (this.user.phone) {
                const phoneNumber = parsePhoneNumberFromString(this.user.phone)
                if (phoneNumber) {
                    account.phone.phone = phoneNumber.nationalNumber
                    account.phone.iso = phoneNumber.number
                    account.phone.calling_code = phoneNumber.countryCallingCode
                    account.phone.country = phoneNumber.country
                }
            }
        },

        refreshValues(payment_intent: PaymentIntent, line_items: LineItem[]): void {
            this.$state.summary = new Map()
            this.$state.pi = {};
            this.$state.line_items = {};

            this.$state.pi = payment_intent;
            this.$state.line_items = line_items;
            this.$state.summary = this.getSummaryItems()
        },

        getLineItemById: function (ticket: Ticket): Ticket {
            return this.line_items.filter((currItem: Ticket) => {
                return currItem.id === ticket.id
            })[0]
        },

        getSummaryItems: function (): Map<string, SummaryItem> | null {

            if (this.summary.size > 0) return this.summary

            const attributesMap: Attributes = new Map()

            const sortedLineItems = [...this.line_items].sort((a, b) => {
                const aIsAddon = a.type === LineItemType.addon
                const bIsAddon = b.type === LineItemType.addon
                if (aIsAddon === bIsAddon) return 0
                return aIsAddon ? 1 : -1
            })

            sortedLineItems.forEach((lineItem: LineItem) => {
                const id = lineItem.product.ticket.id

                let data: Attribute = {
                    price: lineItem.amounts.amount,
                    modified: lineItem.amounts.modified
                }

                if (lineItem.attributes) data.attributes = lineItem.attributes

                if (attributesMap.has(id)) {
                    (attributesMap.get(id) as Attribute[]).push(data)
                } else {
                    attributesMap.set(id, [data])
                }
            })

            const result = new Map()

            attributesMap.forEach((value, key) => {
                const attributes = value
                const lineItem = this.line_items.find((li: LineItem) => li.product.ticket.id === key)

                const total = attributes.reduce((total: number, current: any) => total + current.price, 0)
                const coupon = lineItem.amounts.modifiers
                const total_discount = attributes.reduce((value: number, current: any) => value - current.modified, total)

                const data: SummaryItem = {
                    total,
                    attributes,
                    ticket: lineItem.product.ticket,
                    coupon,
                    total_discount: total - total_discount,
                    type: lineItem.type,
                }

                result.set(key, data)
            })

            return result
        },

        getBarSummaryItems: function (): Map<string, BarSummaryItem> {
            if (!this.bar_items?.length) return new Map()

            const grouped = new Map<string, BarSummaryItem>()

            this.bar_items.forEach((item: BarItem) => {
                const key = String(item.product.id)
                if (grouped.has(key)) {
                    const existing = grouped.get(key)!
                    existing.quantity += 1
                    existing.total += item.price
                } else {
                    grouped.set(key, {
                        product: item.product,
                        quantity: 1,
                        total: item.price,
                        bar_zone: item.bar_zone,
                    })
                }
            })

            return grouped
        },

        resendTickets: async function (
          order_id: string
        ) {

            const {error}: any = await useFetch(`/checkout/resend-tickets`, {
                ...requestOptionsPost,
                body: {
                    order_id,
                }
            })

            if (error.value) {
                throw createError({statusCode: 404, statusMessage: 'api.error.resend_ticket', fatal: false})
            }

            return true
        },

        resetBilling(data: BillingInfo) {
            this.$state.pi.billing =  {
                vat_name: null,
                vat_number: null,
                address: null,
                zip_code: null,
                city: null,
                country: null
            }
        },

        async cancelReservation(redirect = true) {

            const {error}: any = await useFetch(`/pi/${this.pi.id}/cancel`, {
                ...requestOptionsPost,
                body: {}
            })

            if (error.value) {
                return createError({statusCode: 404, statusMessage: 'api.error.cancel', fatal: false})
            }

            if (redirect) {
                await navigateToEvent()
            }
        },
        async updateBuyer(user: string) {
            const {error}: any = await useFetch(`/pi/${this.pi.id}`, {
                ...requestOptionsPost,
                body: { user }
            })

            if (error.value) {
                return createError({statusCode: 404, statusMessage: 'api.error.update_buyer', fatal: false})
            }

            return true
        },
        maxStock: function (ticket: Ticket): number {
          let max = ticket.max_per_order || 15
          if (this.event.max_per_order && this.event.max_per_order < max) {
              max = this.event.max_per_order
          }
          return Math.min(<number>max, <number>ticket.stock)
        },
        enableLoading() {
          this.$state.loading = true;
        },
        disableLoading() {
          this.$state.loading = false
        }

    },
})
